import { useEffect, useState } from "react";
import { Grid, Tab, Tabs } from "@mui/material";

import Box from "../Box";
import Button from "../Button";
import FormField from "../FormField";
import Typography from "../Typography";
import Helpers from "../../commons/helpers";
import useStrings from "../../hooks/useStrings";
import TwoFactorAuthentication from "./twoFactorAuthen";
import ProfileService from "../../services/identity/profile.service";

import { ICodename, IInput } from "../../commons/interfaces";

interface IModelChangePassWord {
    oldPassword?: IInput,
    newPassword?: IInput,
    confirmNewPassword?: IInput,
    otpCode?: IInput,
}

interface IChangePasswordlProps {
    idHref: string;
    tenantCode: string,
    serviceCode: string,
    onError: (msg: string) => void;
    onSuccess: (msg: string) => void;
    onLoading: (value: boolean) => void;
    hidenTabTwoFactorAuthentication?: boolean;
}

const ChangePassword = ({
    idHref,
    tenantCode,
    serviceCode,
    onError,
    onSuccess,
    onLoading,
    hidenTabTwoFactorAuthentication,
}: IChangePasswordlProps) => {
    const strings = useStrings();
    const [model, setModel] = useState<IModelChangePassWord>({} as IModelChangePassWord);

    useEffect(() => {
        let newModel = {
            oldPassword: { value: "" },
            newPassword: { value: "" },
            confirmNewPassword: { value: "" },
        };

        setModel(newModel);
    }, []);


    const onValueChange = (newValue: any, key: string) => {
        setModel({ ...model, [key]: { value: newValue } });
    };

    const checkValidate = () => {
        let checked: boolean = true;
        let newData: any = { ...model };

        if (Helpers.isNullOrEmpty(newData?.["oldPassword"]?.value)) {
            newData["oldPassword"] = { error: strings.Common.REQUIRED };
            checked = false;
        }
        if (Helpers.isNullOrEmpty(newData?.["newPassword"]?.value)) {
            newData["newPassword"] = { error: strings.Common.REQUIRED };
            checked = false;
        }
        if (Helpers.isNullOrEmpty(newData?.["confirmNewPassword"]?.value)) {
            newData["confirmNewPassword"] = { error: strings.Common.REQUIRED };
            checked = false;
        }

        if (newData?.["confirmNewPassword"]?.value !== newData?.["newPassword"]?.value) {
            newData["confirmNewPassword"] = { error: strings.Profile.CONFIRM_NEW_PASSWORD_VALIDATE };
            checked = false;
        }

        if (!checked) {
            setModel(newData);
        }

        return checked;
    };

    const handleChangePassword = async () => {
        if (!checkValidate()) return;

        try {
            onLoading(true);

            const result = await ProfileService.changePassword({
                tenantCode: tenantCode,
                serviceCode: serviceCode,
                oldPassword: model.oldPassword?.value,
                newPassword: model.newPassword?.value,
                confirmPassword: model.confirmNewPassword?.value,
            });

            if (result.statusCode === 200) {
                setModel({
                    oldPassword: { value: "" },
                    newPassword: { value: "" },
                    confirmNewPassword: { value: "" },
                });
                onSuccess(strings.Profile.CHANGE_PASSWORD_SUCCESS);
            }

            onLoading(false);
        } catch (error: any) {
            const e = Helpers.renderExceptionError(error);
            onError(e);
        }
    };

    const renderViewUpdatePassword = () => (
        <Box mt={2}>
            <Typography variant="h5">{strings.Profile.UPDATE_PASSWORD}</Typography>
            <Grid container spacing={3} p={2}>
                <Grid item xs={12}>
                    <FormField
                        secure
                        fullWidth
                        maxLength={255}
                        type={"password"}
                        value={model.oldPassword?.value}
                        label={strings.Profile.OLD_PASSWORD}
                        errorMessage={model.oldPassword?.error}
                        placeholder={strings.Profile.ENTER_OLD_PASSWORD}
                        onChangeValue={(value: any) => onValueChange(value, "oldPassword")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        secure
                        fullWidth
                        maxLength={255}
                        type={"password"}
                        value={model.newPassword?.value}
                        label={strings.Profile.NEW_PASSWORD}
                        errorMessage={model.newPassword?.error}
                        placeholder={strings.Profile.ENTER_NEW_PASSWORD}
                        onChangeValue={(value: any) => onValueChange(value, "newPassword")}
                    />
                </Grid>
                <Grid item xs={12}>
                    <FormField
                        secure
                        fullWidth
                        maxLength={255}
                        type={"password"}
                        value={model.confirmNewPassword?.value}
                        label={strings.Profile.CONFIRM_NEW_PASSWORD}
                        errorMessage={model.confirmNewPassword?.error}
                        placeholder={strings.Profile.ENTER_CONFIRM_NEW_PASSWORD}
                        onChangeValue={(value: any) =>
                            onValueChange(value, "confirmNewPassword")
                        }
                    />
                </Grid>
                <Grid item xs={12}>
                    <Box display="flex" justifyContent="flex-end" gap={1}>
                        {(!Helpers.isNullOrEmpty(model.oldPassword?.value) ||
                            !Helpers.isNullOrEmpty(model.newPassword?.value) ||
                            !Helpers.isNullOrEmpty(model.confirmNewPassword?.value)) &&
                            (
                                <Button
                                    color="secondary"
                                    onClick={() => {
                                        setModel({
                                            oldPassword: { value: "" },
                                            newPassword: { value: "" },
                                            confirmNewPassword: { value: "" },
                                        });
                                    }}>
                                    {strings.Common.CANCEL}
                                </Button>
                            )
                        }
                        <Button
                            onClick={handleChangePassword}
                            disabled={
                                !(!Helpers.isNullOrEmpty(model.oldPassword?.value) ||
                                    !Helpers.isNullOrEmpty(model.newPassword?.value) ||
                                    !Helpers.isNullOrEmpty(model.confirmNewPassword?.value))
                            }
                        >
                            {strings.Common.UPDATE}
                        </Button>
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );

    const [valueTab, setValueTab] = useState(0);
    const arrTab: ICodename[] = [
        { code: "code1", name: strings.Profile.PASSWORD, },
        { code: "code2", name: strings.TwoFactorAuth.AUTHENTICATION, },
    ];

    return (
        <Box key={`cp`} id={idHref} p={2}>
            {!hidenTabTwoFactorAuthentication &&
                <>
                    <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                        <Tabs
                            value={valueTab}
                            variant="scrollable"
                            scrollButtons="auto"
                            aria-label="scrollable auto tabs example"
                            onChange={(event: any, newValue: number) => {
                                setValueTab(newValue);
                            }}
                        >
                            {
                                arrTab.map((item, index: number) => (
                                    <Tab key={`${item.code}_${index}`} label={item.name} />
                                ))
                            }
                        </Tabs>
                    </Box>
                    {(valueTab === 0)
                        ? renderViewUpdatePassword()
                        : (
                            <Box mt={2}>
                                <TwoFactorAuthentication
                                    idHref={"two_factor_authen"}
                                    onError={onError}
                                    onSuccess={onSuccess}
                                    onLoading={onLoading}
                                />
                            </Box>
                        )
                    }
                </>
            }
            {hidenTabTwoFactorAuthentication &&
                renderViewUpdatePassword()
            }
        </Box >
    );
};

export default ChangePassword;
